import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';
import { MAT_PAGINATOR_DEFAULT_OPTIONS, MatPaginator, MatPaginatorDefaultOptions, MatPaginatorIntl } from '@angular/material/paginator';
import { range } from 'lodash';
import { distinctUntilChanged } from 'rxjs/operators';



interface PageItem {
  index: number;
  isEllipsis?: boolean;
}

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaginationComponent extends MatPaginator implements OnInit, OnChanges {
  pages: Array<{
    index: number;
    isEllipsis?: boolean;
  }> = [];

  constructor(
    intl: MatPaginatorIntl,
    private changeDetectorRef: ChangeDetectorRef,
    @Optional() @Inject(MAT_PAGINATOR_DEFAULT_OPTIONS) defaults?: MatPaginatorDefaultOptions
  ) {
    super(intl, changeDetectorRef, defaults);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.page
      .pipe(
        distinctUntilChanged()
      )
      .subscribe((): void => {
        this.generatePages();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.length && changes.length.currentValue !== changes.length.previousValue) {
      this.generatePages();
    }
  }

  goToPage(index: number): void {
    const previousPageIndex = this.pageIndex;

    this.pageIndex = index;
    this.page.emit({
      previousPageIndex,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.length
    });
    this.changeDetectorRef.markForCheck();
  }

  private generatePages(): void {
    const pageIndex = this.pageIndex,
      pageSize = this.pageSize,
      total = this.length;
    let pages: Array<{
      index: number;
      isEllipsis?: boolean;
    }> = [];
    let pageCount: number = Math.ceil(total / pageSize);

    pageCount = (Math.max(isNaN(pageCount) ? 0 : pageCount, 1));

    if (pageCount < 8) {
      pages = pages.concat(this.getPageRange(0, 7, pageCount - 1));
    } else {
      pages.push({
        index: 0
      });

      let siblings: Array<PageItem> = [];

      if (pageIndex <= 2) {
        siblings = this.getPageRange(1, 4, pageCount - 2)
      } else if (pageIndex >= pageCount - 3) {
        siblings = this.getPageRange(pageCount - 5, pageCount - 2, pageCount - 2)
      } else {
        siblings = this.getPageRange(pageIndex - 2, pageIndex + 2, pageCount - 2);
      }
      if (siblings[0].index - pages[pages.length - 1].index > 2) {
        pages.push({
          index: Math.max(siblings[0].index - 3, 2),
          isEllipsis: true
        });
      } else if (siblings[0].index - pages[pages.length - 1].index === 2) {
        pages.push({
          index: 1
        });
      }
      pages = pages.concat(siblings);
      if (pageCount - 1 - siblings[siblings.length - 1].index > 2) {
        pages.push({
          index: Math.min(pages[pages.length - 1].index + 3, pageCount - 2),
          isEllipsis: true
        });
      } else if (pageCount - 1 - siblings[siblings.length - 1].index === 2) {
        pages.push({
          index: pageCount - 2
        });
      }
      pages.push({
        index: pageCount - 1
      });
    }
    this.pages = pages;
    this.changeDetectorRef.markForCheck();
  }

  private getPageRange(start: number, end: number, max: number): Array<PageItem> {
    return range(start, Math.min(end, max) + 1).map((item) => {
      return {
        index: item
      };
    });
  }
}
